import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import InputAdornment from '@material-ui/core/InputAdornment';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
    StyledCard,
    StyledCardContent,
    Title,
    Text,
    ContainedButton,
    OutlinedButton,
    InputWithEndAdornment,
    SearchIcon,
    Checkbox,
    CheckboxGroup,
    FormGroup,
    Avatar,
    NoInformation,
    Progress,
} from '../../index.js';

import { StyledDialog, StyledPaper, StyledMindGymIconContainer } from './style';

import { GET_PATIENTS_AND_PRIMARY_PATIENTS } from '../../../services/provider';

import { alertError } from '../../../redux/stateActions';

const MindGymIcon = '/resources/images/mindgym/mindgym-icon.svg';

const AddPatientDialog = (props) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [selectedPatients, setSelectedPatients] = useState([]);
    const [searchValue, setSearchValue] = useState('');

    /*--------------- get Patients and Primary Patients ---------------*/
    const [patientListData, setPatientListData] = useState([]);

    const filteredPatient = useMemo(() => {
        const patientData = !_.isEmpty(props.options) ? props.options : patientListData;

        if (searchValue === '') {
            return patientData;
        } else {
            const newPatientList = [...patientData].filter(
                (patient) =>
                    patient?.HNCode?.toLowerCase().includes(searchValue.toLowerCase()) ||
                    patient?.user?.fullname?.toLowerCase().includes(searchValue.toLowerCase())
            );
            return newPatientList;
        }
    }, [patientListData, searchValue, props.options]);

    const { loading: getAllPatientsLoading } = useQuery(GET_PATIENTS_AND_PRIMARY_PATIENTS, {
        variables: {
            hospitalId: user.staff.hospital.id,
            staffId: user.staff.id,
        },
        skip: _.isEmpty(user) || !_.isEmpty(props.options),
        onCompleted: (res) => {
            const concatPatients = _.concat(
                res.staff.primaryPatients,
                res.hospital.patients.filter((p) => p.user !== null)
            );

            let filteredList = [...new Set(concatPatients.map(JSON.stringify))].map(JSON.parse);

            setPatientListData(filteredList);
        },
        onError: (res) => {
            dispatch(alertError(`Cannot get Client and Primary Client: ${res.message}`));
        },
    });

    useEffect(() => {
        if (props.open) {
            setSelectedPatients(props.selectedPatients);
        }
    }, [props.open, props.selectedPatients]);

    const handleSelect = (patient) => {
        if (selectedPatients.find((p) => p.id === patient.id)) {
            const newSelectedPatient = [...selectedPatients];
            newSelectedPatient.splice(
                selectedPatients.findIndex((p) => p.id === patient.id),
                1
            );
            setSelectedPatients(newSelectedPatient);
        } else {
            setSelectedPatients([
                ...selectedPatients,
                { ...patient, startTime: '', endTime: '', credit: 0, selected: true },
            ]);
        }
    };

    const handleCancel = () => {
        props.onCancel();
        setSelectedPatients([]);
        setSearchValue('');
    };

    const handleAdd = () => {
        props.onAdd(selectedPatients);
        props.onCancel();
        setSearchValue('');
    };

    return (
        <StyledDialog
            scroll="body"
            open={props.open}
            onClose={props.onClose}
            aria-labelledby="AddClientDialog"
            aria-describedby="Add client to appointment"
            PaperComponent={StyledPaper}
        >
            <StyledCard padding="20px">
                <StyledCardContent
                    style={{ display: 'flex', flexDirection: 'column', rowGap: '15px' }}
                >
                    <div>
                        <Text $fontSize="20px" $bold>
                            {props.title || 'Add Client'}
                        </Text>
                    </div>
                    <div style={{ position: 'relative' }}>
                        {getAllPatientsLoading && <Progress />}
                        <InputWithEndAdornment
                            id="search"
                            name="search"
                            placeholder="Search"
                            startAdornment={
                                <InputAdornment
                                    position="start"
                                    style={{ opacity: 0.6, marginLeft: '10px' }}
                                >
                                    <SearchIcon fill="gray2" />
                                </InputAdornment>
                            }
                            type="text"
                            style={{ height: '32px' }}
                            inputPropsStyle={{ paddingLeft: '15px' }}
                            onChange={(e) => setSearchValue(e.target.value)}
                            value={searchValue}
                            autoComplete="off"
                            inputProps={{ maxLength: 100 }}
                        />
                        <CheckboxGroup component="fieldset" style={{ margin: 0, height: '500px' }}>
                            <FormGroup
                                row
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    rowGap: '10px',
                                    maxHeight: '500px',
                                    overflowX: 'auto',
                                    paddingBottom: '10px',
                                }}
                            >
                                {filteredPatient && filteredPatient.length > 0 ? (
                                    filteredPatient.map((patient) => {
                                        const foundPatient = selectedPatients.find(
                                            (p) => p.id === patient.id
                                        );

                                        return (
                                            <div
                                                key={patient.id}
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'auto auto 1fr 150px auto',
                                                    columnGap: '15px',
                                                    width: '100%',
                                                    alignItems: 'center',
                                                    cursor: foundPatient?.disabled
                                                        ? 'not-allowed'
                                                        : 'pointer',
                                                }}
                                                onClick={() => {
                                                    if (foundPatient?.disabled) return;
                                                    handleSelect(patient);
                                                }}
                                            >
                                                <Checkbox
                                                    fill={
                                                        foundPatient?.disabled
                                                            ? 'gray4'
                                                            : 'primaryBlue'
                                                    }
                                                    checked={foundPatient ? true : false}
                                                />
                                                <Avatar
                                                    alt={`${patient?.user?.fullname}`}
                                                    src={
                                                        patient?.user?.avatar
                                                            ? patient?.user?.avatar?.url
                                                            : null
                                                    }
                                                    $avatarSize="48px"
                                                />
                                                <div
                                                    style={{
                                                        width: '200px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Title
                                                        style={{
                                                            margin: '4px 0 8px',
                                                            wordSpacing: '400px',
                                                        }}
                                                        $fontSize="16px"
                                                        $ellipsis
                                                        $lineHeight="20px"
                                                    >{`${patient?.user?.fullname}`}</Title>
                                                </div>

                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Text>{patient?.HNCode}</Text>
                                                </div>
                                                {patient?.user?.uuid ? (
                                                    <StyledMindGymIconContainer>
                                                        <img src={MindGymIcon} alt="MindGymIcon" />
                                                    </StyledMindGymIconContainer>
                                                ) : (
                                                    <StyledMindGymIconContainer
                                                        style={{ backgroundColor: 'transparent' }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <NoInformation height="200px" />
                                )}
                            </FormGroup>
                        </CheckboxGroup>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            columnGap: '10px',
                        }}
                    >
                        <OutlinedButton
                            style={{ height: '32px', width: '80px' }}
                            onClick={handleCancel}
                        >
                            Cancel
                        </OutlinedButton>
                        <ContainedButton
                            style={{ height: '32px', width: '96px' }}
                            disabled={selectedPatients.length === 0}
                            onClick={handleAdd}
                        >
                            Add
                        </ContainedButton>
                    </div>
                </StyledCardContent>
            </StyledCard>
        </StyledDialog>
    );
};

AddPatientDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    selectedPatients: PropTypes.array,
    onCancel: PropTypes.func,
    onAdd: PropTypes.func,
};

export default AddPatientDialog;
